import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ OPS-01, OPS-02 and OPS-10 at v1.996, crew part 4A: a job's tasks — one per labour or
// transport line, kept in step by the database, or made by hand — and the shifts that carry them.
// How full a task is, how it fits a shift and whether a mismatch may be saved are the database's.

export type Task = components['schemas']['TaskView'];
export type TaskAssignment = components['schemas']['TaskAssignmentView'];
export type TaskCandidate = components['schemas']['TaskCandidateView'];
export type TaskStatus = Task['status'];

export type TaskInput = {
  jobId?: string | null;
  description: string;
  roleId?: string | null;
  window: { start: string; end: string };
  venueId?: string | null;
  roomId?: string | null;
  crewNeeded?: number;
  crewNotes?: string | null;
  officeNotes?: string | null;
};

export const TASK_STATUS: Record<TaskStatus, string> = {
  open: 'Open',
  covered: 'Covered',
  done: 'Done',
  not_needed: 'Not needed',
};

/** OPS-10: how a task sits in a shift, in a word. */
export const FIT: Record<TaskCandidate['fit'], string> = {
  fits: 'Fits',
  partial: 'Partial',
  outside: 'Outside',
};

/** A refused assignment that the office may confirm: the database's mismatch (OPS-10). */
export class MismatchProblem extends ApiProblem {
  constructor(
    detail: string,
    readonly fit: TaskCandidate['fit'] | null,
    readonly venueDiffers: boolean,
  ) {
    super('task-mismatch', detail);
  }
}

function problem(error: unknown, fallback: string): ApiProblem {
  if (isProblemDetails(error) && error.type.endsWith('task-mismatch')) {
    const extra = error as unknown as { fit?: TaskCandidate['fit'] | null; venueDiffers?: boolean };
    return new MismatchProblem(
      error.detail ?? error.title,
      extra.fit ?? null,
      !!extra.venueDiffers,
    );
  }
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const jobTasksQuery = (jobId: string) =>
  queryOptions({
    queryKey: ['jobs', jobId, 'tasks'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{jobId}/tasks', {
        params: { path: { jobId } },
      });
      if (error) throw problem(error, 'Could not load this job’s tasks.');
      return data;
    },
  });

export const taskCandidatesQuery = (taskId: string) =>
  queryOptions({
    queryKey: ['tasks', taskId, 'candidates'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/tasks/{id}/candidates', {
        params: { path: { id: taskId } },
      });
      if (error) throw problem(error, 'Could not load the shifts for this task.');
      return data;
    },
  });

export async function createTask(input: TaskInput) {
  const { data, error } = await api.POST('/v1/tasks', { body: input });
  if (error) throw problem(error, 'Could not add that task.');
  return data;
}

export async function updateTask(id: string, changes: Partial<Omit<TaskInput, 'jobId'>>) {
  const { data, error } = await api.PATCH('/v1/tasks/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save that task.');
  return data;
}

export async function setTaskStatus(id: string, status: TaskStatus) {
  const { data, error } = await api.POST('/v1/tasks/{id}/status', {
    params: { path: { id } },
    body: { status },
  });
  if (error) throw problem(error, 'Could not change that task.');
  return data;
}

/** Throws a MismatchProblem when the task doesn't fit the shift and it wasn't confirmed. */
export async function assignTask(id: string, shiftId: string, confirmMismatch = false) {
  const { data, error } = await api.POST('/v1/tasks/{id}/assignments', {
    params: { path: { id } },
    body: { shiftId, confirmMismatch },
  });
  if (error) throw problem(error, 'Could not assign that task.');
  return data;
}

export async function unassignTask(id: string, shiftId: string) {
  const { data, error } = await api.DELETE('/v1/tasks/{id}/assignments/{shiftId}', {
    params: { path: { id, shiftId } },
  });
  if (error) throw problem(error, 'Could not take it off that shift.');
  return data;
}
